import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useSale, useSaleOperations } from '../../hooks/useSales'
import { getImageUrl } from '../../services/cloudinary.service'
import { generateSalePDF } from '../../utils/pdfGenerator'
import { notifyOrderCreated } from '../../services/notifications.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import { formatCurrency, formatDate } from '../../utils/helpers'
import './SaleDetail.css'

function SaleDetail() {
  const { id } = useParams()
  const { sale, loading, error, refetch } = useSale(id)
  const { update, loading: updating } = useSaleOperations()
  const [notification, setNotification] = useState(null)
  const [resending, setResending] = useState(false)

  const isMercadoPagoSale =
    sale?.paymentGateway?.provider === 'mercadopago' ||
    sale?.payment?.type === 'gateway_mercadopago'

  const latestPayment =
    sale?.payments && sale.payments.length > 0
      ? sale.payments[sale.payments.length - 1]
      : null

  const paymentGatewayStatus = sale?.paymentGateway?.status || null

  const getGatewayStatusLabel = (status) => {
    if (!status) return 'Estado de pago desconocido'
    if (status === 'approved') return 'Pago aprobado'
    if (status === 'in_process') return 'Pago en proceso'
    if (status === 'pending' || status === 'created') return 'Pago pendiente'
    if (status === 'rejected') return 'Pago rechazado'
    if (status === 'cancelled') return 'Pago cancelado'
    if (status === 'refunded') return 'Pago reembolsado'
    return `Estado: ${status}`
  }

  const formatDateTime = (value) => {
    if (!value) return ''
    if (value.toDate) {
      return value.toDate().toLocaleString()
    }
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleString()
  }

  const historyEvents = (() => {
    if (!sale) return []

    const events = []

    if (sale.createdAt) {
      events.push({
        key: 'created',
        title: 'Venta creada',
        description: sale.orderNumber
          ? `Venta generada para el pedido ${sale.orderNumber}`
          : 'Venta generada',
        timestamp: sale.createdAt
      })
    }

    if (sale.paid || paymentGatewayStatus === 'approved') {
      const paymentTimestamp =
        sale.paymentGateway?.updatedAt ||
        (latestPayment?.receivedAt && latestPayment.receivedAt.value) ||
        sale.updatedAt

      events.push({
        key: 'paid',
        title: 'Pago confirmado',
        description: isMercadoPagoSale
          ? 'Pago confirmado a través de MercadoPago'
          : 'Venta marcada como cobrada',
        timestamp: paymentTimestamp
      })
    }

    if (sale.status === 'pending_delivery' || sale.status === 'completed' || sale.status === 'cancelled') {
      const statusLabels = {
        pending_delivery: 'Pendiente Entrega',
        completed: 'Completada',
        cancelled: 'Cancelada'
      }

      events.push({
        key: 'status',
        title: 'Estado actualizado',
        description: `Estado cambiado a: ${statusLabels[sale.status] || sale.status}`,
        timestamp: sale.updatedAt
      })
    }

    return events
      .filter(event => event.timestamp)
      .sort((a, b) => {
        const aDate = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp)
        const bDate = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp)
        return bDate - aDate
      })
  })()

  const handleMarkPaid = async () => {
    if (!sale) return
    await update(sale.id || id, { paid: true })
    refetch()
  }

  const handleMarkDelivered = async () => {
    if (!sale) return
    try {
      await update(sale.id || id, { status: 'completed' })
      setNotification({
        type: 'success',
        message: 'Venta marcada como entregada'
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al marcar la venta como entregada'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleResendConfirmation = async () => {
    if (!sale?.orderId || resending) return
    try {
      setResending(true)
      await notifyOrderCreated(sale.orderId, { force: true })
      setNotification({
        type: 'success',
        message: 'Confirmación de pedido reenviada al cliente'
      })
      setTimeout(() => setNotification(null), 4000)
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al reenviar la confirmación'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setResending(false)
    }
  }

  if (loading) {
    return (
      <div className="sale-detail-page">
        <div className="loading-state">
          <p>Cargando venta...</p>
        </div>
      </div>
    )
  }

  if (error || !sale) {
    return (
      <div className="sale-detail-page">
        <div className="error-state">
          <p>Error: {error || 'Venta no encontrada'}</p>
          <Link to="/admin/ventas">
            <Button>Volver a Ventas</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="sale-detail-page">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title-row">
            <h1>Venta {sale.saleNumber || '-'}</h1>
            <span
              className={`status-pill ${
                sale.status === 'completed'
                  ? 'status-completed'
                  : sale.status === 'cancelled'
                    ? 'status-cancelled'
                    : sale.status === 'pending_delivery'
                      ? 'status-pending-delivery'
                      : sale.status === 'pending_payment'
                        ? 'status-pending-payment'
                        : 'status-pending'
              }`}
            >
              {sale.status === 'completed'
                ? 'Completada'
                : sale.status === 'cancelled'
                  ? 'Cancelada'
                  : sale.status === 'pending_delivery'
                    ? 'Pendiente Entrega'
                    : sale.status === 'pending_payment'
                      ? 'Pendiente Pago'
                      : 'Pendiente'}
            </span>
          </div>
          <p className="related-order">
            Pedido relacionado:{' '}
            {sale.orderId ? (
              <Link to={`/admin/pedidos/${sale.orderId}`} className="order-link">
                {sale.orderNumber || sale.orderId}
              </Link>
            ) : (
              sale.orderNumber || '-'
            )}
          </p>
        </div>
        <div className="page-header-actions">
          <Link to="/admin/ventas">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Link to={`/admin/ventas/nuevo/${sale.orderId}`}>
            <Button variant="secondary">✏️ Editar Venta</Button>
          </Link>
          {sale.orderId && (
            <Link to={`/admin/pedidos/${sale.orderId}`}>
              <Button variant="secondary">📦 Ver Pedido</Button>
            </Link>
          )}
          {sale.status !== 'completed' && sale.status !== 'cancelled' && (
            <Button
              variant="primary"
              onClick={handleMarkDelivered}
              disabled={updating}
            >
              📦 Marcar como Entregada
            </Button>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-main">
          <div className="detail-top-cards">
            <div className="info-card">
              <h3>Información General</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Fecha de Venta:</label>
                  <span>{formatDate(sale.saleDate)}</span>
                </div>
                <div className="info-item">
                  <label>Estado de Pago:</label>
                  <span
                    className="payment-status-text"
                    style={{
                      color: sale.paid ? '#28a745' : '#dc3545'
                    }}
                  >
                    {sale.paid ? '💵 Cobrada' : '⏳ Sin Cobrar'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Método de Pago:</label>
                  <span className="payment-method-badge">
                    {sale.paymentMethod === 'efectivo'
                      ? '💵'
                      : sale.paymentMethod === 'transferencia'
                        ? '🏦'
                        : sale.paymentMethod === 'tarjeta'
                          ? '💳'
                          : sale.paymentMethod === 'cheque'
                            ? '📝'
                            : sale.paymentMethod === 'pendiente'
                              ? '⏳'
                              : '💰'}
                    {sale.paymentMethod || '-'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Registrada por:</label>
                  <span>{sale.createdByName || 'Sistema'}</span>
                </div>
                <div className="info-item">
                  <label>Número de Venta:</label>
                  <span>
                    <strong>{sale.saleNumber || '-'}</strong>
                  </span>
                </div>
                <div className="info-item">
                  <label>Número de Pedido:</label>
                  {sale.orderId ? (
                    <Link to={`/admin/pedidos/${sale.orderId}`} className="order-link">
                      {sale.orderNumber || sale.orderId}
                    </Link>
                  ) : (
                    <span>{sale.orderNumber || '-'}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>Cliente</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Nombre:</label>
                  <span>{sale.customer?.name || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{sale.customer?.email || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Teléfono:</label>
                  <span>{sale.customer?.phone || '-'}</span>
                </div>
                {sale.customer?.address && (
                  <div className="info-item full-width">
                    <label>Dirección:</label>
                    <span>
                      {sale.customer.address.street || ''} {sale.customer.address.number || ''}
                      {sale.customer.address.city ? `, ${sale.customer.address.city}` : ''}
                      {sale.customer.address.province ? `, ${sale.customer.address.province}` : ''}
                      {sale.customer.address.postalCode ? ` (${sale.customer.address.postalCode})` : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="products-card">
            <div className="products-header">
              <h3>Productos</h3>
              {sale.items && sale.items.length > 0 && (
                <span className="items-count">
                  {sale.items.length} {sale.items.length === 1 ? 'item' : 'items'}
                </span>
              )}
            </div>
            {sale.items && sale.items.length > 0 ? (
              <div className="items-list">
                {sale.items.map((item, index) => {
                  const imageUrl = item.image
                    ? getImageUrl(item.image, { width: 100, height: 100, crop: 'fill' })
                    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'

                  return (
                    <div key={index} className="sale-item">
                      <img src={imageUrl} alt={item.productName} className="item-image" />
                      <div className="item-info">
                        <h4 className="item-name">{item.productName}</h4>
                        {item.sku && <p className="item-sku">SKU: {item.sku}</p>}
                        {item.variant && (
                          <p className="item-variant">
                            {item.variant.type}: {item.variant.name}
                          </p>
                        )}
                        <p className="item-quantity">Cantidad: {item.quantity}</p>
                      </div>
                      <div className="item-price">
                        <p className="item-unit-price">{formatCurrency(item.price)} c/u</p>
                        <p className="item-total-price">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="no-items">No hay productos en esta venta</p>
            )}
          </div>

          <div className="history-card">
            <h3>Historial de Actividad</h3>
            {historyEvents.length === 0 ? (
              <p className="no-history">No hay actividad registrada para esta venta</p>
            ) : (
              <div className="history-list">
                {historyEvents.map((event) => (
                  <div key={event.key} className="history-item">
                    <div className="history-dot" />
                    <div className="history-content">
                      <div className="history-title">{event.title}</div>
                      <div className="history-meta">
                        <span className="history-date">{formatDateTime(event.timestamp)}</span>
                        {event.description && (
                          <span className="history-description">{event.description}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="detail-side">
          <div className="financial-card">
            <h3>Resumen Financiero</h3>
            <div className="financial-summary">
              <div className="summary-row">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">{formatCurrency(sale.subtotal || 0)}</span>
              </div>
              {sale.discount > 0 && (
                <div className="summary-row discount">
                  <span className="summary-label">Descuento:</span>
                  <span className="summary-value">-{formatCurrency(sale.discount || 0)}</span>
                </div>
              )}
              {sale.taxes > 0 && (
                <div className="summary-row">
                  <span className="summary-label">Impuestos:</span>
                  <span className="summary-value">{formatCurrency(sale.taxes || 0)}</span>
                </div>
              )}
              {sale.shipping > 0 && (
                <div className="summary-row">
                  <span className="summary-label">Envío:</span>
                  <span className="summary-value">{formatCurrency(sale.shipping || 0)}</span>
                </div>
              )}
              <div className="summary-row total">
                <span className="summary-label">Total:</span>
                <span className="summary-value">{formatCurrency(sale.total || 0)}</span>
              </div>
            </div>

            {isMercadoPagoSale && (
              <div className="mp-payment-summary">
                <div className="mp-payment-main">
                  <span className="mp-provider">MercadoPago</span>
                  <span
                    className={`mp-status-pill ${
                      paymentGatewayStatus === 'approved'
                        ? 'mp-status-approved'
                        : paymentGatewayStatus === 'rejected' || paymentGatewayStatus === 'cancelled'
                          ? 'mp-status-error'
                          : 'mp-status-pending'
                    }`}
                  >
                    {getGatewayStatusLabel(paymentGatewayStatus)}
                  </span>
                </div>
                {latestPayment && (
                  <div className="mp-payment-extra">
                    {latestPayment.paymentId && (
                      <div className="mp-extra-row">
                        <span className="mp-label">ID de pago:</span>
                        <span className="mp-value">{latestPayment.paymentId}</span>
                      </div>
                    )}
                    {latestPayment.transactionAmount != null && (
                      <div className="mp-extra-row">
                        <span className="mp-label">Monto:</span>
                        <span className="mp-value">
                          {formatCurrency(latestPayment.transactionAmount || 0)}{' '}
                          {latestPayment.currencyId || ''}
                        </span>
                      </div>
                    )}
                    {latestPayment.receivedAt?.value && (
                      <div className="mp-extra-row">
                        <span className="mp-label">Fecha acreditación:</span>
                        <span className="mp-value">
                          {formatDateTime(latestPayment.receivedAt.value)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="documents-card">
            <h3>Documentos y Acciones</h3>
            <div className="documents-actions">
              <Button
                variant="primary"
                onClick={async () => {
                  await generateSalePDF(sale)
                }}
              >
                📄 Generar PDF de la Venta
              </Button>
              {sale.orderId && (
                <Button
                  variant="secondary"
                  onClick={handleResendConfirmation}
                  disabled={resending}
                >
                  {resending ? 'Reenviando...' : 'Reenviar Confirmación'}
                </Button>
              )}
              {!sale.paid && (
                <Button
                  variant="outline"
                  onClick={handleMarkPaid}
                  disabled={updating}
                >
                  💵 Marcar como Cobrada
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {sale.paymentProofs && sale.paymentProofs.length > 0 && (
        <div className="proofs-card">
          <h3>Comprobantes de Pago</h3>
          <div className="proofs-grid">
            {sale.paymentProofs.map((proof, index) => (
              <div key={index} className="proof-item">
                <img
                  src={getImageUrl(proof.fileId || proof.url, { width: 300, height: 300, crop: 'fill' })}
                  alt={`Comprobante ${index + 1}`}
                  onClick={() => window.open(getImageUrl(proof.fileId || proof.url), '_blank')}
                  className="proof-image"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {sale.notes && (
        <div className="notes-card">
          <h3>Notas</h3>
          <p className="notes-text">{sale.notes}</p>
        </div>
      )}

      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default SaleDetail

